import {
  CreateUnnamedRetentionMetricRequest,
  GetAllRetentionMetricsResponse,
  GetUnnamedRetentionMetricResponse,
  RetentionMetricDetail,
  UnnamedRetentionMetric,
} from '@fullstory/proto/fs/services/dx-objects';
import { createRequestReducer, DispatchedAction, RequestAction } from '@fullstory/redux-util';
import { createAppCacheReducer } from '@fullstory/redux-util/utils/appCache/reducers';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { isEqual, mapKeys } from 'lodash';
import { Action } from 'redux';
import { getObjectWithOverlayStorageId } from '../../utils';
import { createAccessReducer } from '../../utils/accessControl';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { HomeRequestActions, HomeRequestTypes } from '../home';
import { RetentionMetricsActions } from './actions';
import {
  ComputeRetentionMetricResponseWithId,
  RetentionMetricsById,
  RetentionMetricsRequestTypes,
  RetentionMetricsState,
} from './types';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const getRetentionMetricId = (retentionMetric: RetentionMetricDetail | null): string | undefined =>
  retentionMetric?.retentionMetric?.retentionMetric?.id;

export const retentionMetrics = (
  state: RetentionMetricsState = {},
  action:
    | RetentionMetricsActions
    | AppBackendActions
    | HomeRequestActions
    | SpaceActions
    | BulkRequestActions,
): RetentionMetricsState => {
  switch (action.type) {
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: {
      if (!action.data?.deps?.retentionMetrics) {
        return state;
      }
      if (action.data.overlay && action.data.deps.retentionMetrics) {
        const retentionMetrics = action.data.deps.retentionMetrics;
        const overlayId = action.data.overlay.id;
        const updatedRetentionMetrics = mapKeys(retentionMetrics, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedRetentionMetrics };
      }

      const newState = { ...state };

      for (const retentionMetricId in action.data?.deps?.retentionMetrics) {
        if (!retentionMetricId) {
          // Skip undefined ids
          continue;
        }

        const retentionMetric = action.data.deps.retentionMetrics[retentionMetricId];
        if (!retentionMetric) {
          // Skip undefined retention metrics
          continue;
        }

        if (isEqual(state[retentionMetricId], retentionMetric)) {
          // Do not set if the object is already deep equal to the proposed object.
          continue;
        }

        newState[retentionMetricId] = retentionMetric;
      }

      return newState;
    }
    case RetentionMetricsRequestTypes.GetAllRetentionMetricsSuccess: {
      return action.data.retentionMetrics.reduce<RetentionMetricsById>(
        (acc, val) => {
          const id = getRetentionMetricId(val);
          if (id !== undefined && !isEqual(acc[id], val)) acc[id] = val;
          return acc;
        },
        { ...state },
      );
    }
    case HomeRequestTypes.DuplicateHomeCardSuccess: {
      const innerRetentionMetric = action.data.retention ?? null;
      const id = getRetentionMetricId(innerRetentionMetric);
      return innerRetentionMetric === null || id === undefined
        ? state
        : { ...state, [id]: innerRetentionMetric };
    }
    case RetentionMetricsRequestTypes.SetRetentionMetricSuccess: {
      const { retentionMetric } = action.data;
      const id = getRetentionMetricId(retentionMetric);

      return retentionMetric === null || id === undefined
        ? state
        : { ...state, [id]: retentionMetric };
    }
    case RetentionMetricsRequestTypes.MarkViewedSuccess: {
      const { body } = action.request;
      if (body === undefined) throw new Error('Invariant violation: Request body must exist.');

      const { lastViewed } = action.data;
      const { retentionMetricId } = body;
      const retentionMetric = state[retentionMetricId];

      return retentionMetric == null
        ? state
        : { ...state, [retentionMetricId]: { ...retentionMetric, lastViewed } };
    }
    case RetentionMetricsRequestTypes.StarRetentionMetricSuccess: {
      const { body } = action.request;
      if (body === undefined) throw new Error('Invariant violation: Request body must exist.');

      const { retentionMetricId, starred } = body;
      const retentionMetric = state[retentionMetricId];

      return retentionMetric == null
        ? state
        : { ...state, [retentionMetricId]: { ...retentionMetric, starred } };
    }
    case RetentionMetricsRequestTypes.ArchiveRetentionMetricSuccess: {
      const { body } = action.request;
      if (body === undefined) throw new Error('Invariant violation: Request body must exist.');

      const { retentionMetricId, archived } = body;
      const retentionMetric = state[retentionMetricId];
      const retentionMetricRecord = retentionMetric?.retentionMetric;
      const metadata = retentionMetricRecord?.metadata;

      return retentionMetric == null || retentionMetricRecord == null || metadata == null
        ? state
        : {
            ...state,
            [retentionMetricId]: {
              ...retentionMetric,
              retentionMetric: {
                ...retentionMetricRecord,
                metadata: { ...metadata, archived },
              },
            },
          };
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const retentionMetricsReq = body.retentionMetrics;

      if (retentionMetricsReq != null) {
        retentionMetricsReq.retentionMetrics.forEach(m => {
          const { retentionMetricId, archived } = m;
          const retentionMetric = newState?.[retentionMetricId];
          const retentionMetricRecord = retentionMetric?.retentionMetric;
          const metadata = retentionMetricRecord?.metadata;

          if (retentionMetric && retentionMetricRecord && metadata) {
            newState = {
              ...newState,
              [retentionMetricId]: {
                ...retentionMetric,
                retentionMetric: {
                  ...retentionMetricRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (
          object?.retentionMetric &&
          object.retentionMetric.retentionMetric?.retentionMetric?.id
        ) {
          updated[object.retentionMetric.retentionMetric.retentionMetric.id] =
            object.retentionMetric;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const retentions = action.data.objects.reduce<RetentionMetricsState>((acc, o) => {
        if (o.retentionMetric?.retentionMetric?.retentionMetric?.id)
          acc[o.retentionMetric?.retentionMetric?.retentionMetric?.id] = o.retentionMetric;
        return acc;
      }, {});

      return Object.keys(retentions).length
        ? {
            ...state,
            ...retentions,
          }
        : state;
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
      if (!action.data.deps?.retentionMetrics) return state;
      if (action.data.overlay && action.data.deps.retentionMetrics) {
        const retentionMetrics = action.data.deps.retentionMetrics;
        const overlayId = action.data.overlay.id;
        const updatedRetentionMetrics = mapKeys(retentionMetrics, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedRetentionMetrics };
      }
      return { ...state, ...action.data.deps.retentionMetrics };
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.retentionMetrics) return state;
      return { ...state, ...action.data.deps.retentionMetrics };
    }
  }
  return state;
};

const data = createAppCacheReducer<ComputeRetentionMetricResponseWithId>()(
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheIsLoading,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheLoadFailed,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheRefetching,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheHasData,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheFresh,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheSwap,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheClear,
)();
const allRetentionMetrics = createRequestReducer<GetAllRetentionMetricsResponse>()(
  RetentionMetricsRequestTypes.GetAllRetentionMetricsRequest,
  RetentionMetricsRequestTypes.GetAllRetentionMetricsSuccess,
  RetentionMetricsRequestTypes.GetAllRetentionMetricsFailure,
);

type UnnamedRetentionMetricsState = { [hash: string]: UnnamedRetentionMetric };
const unnamedRetentionMetricsRequests = createRequestReducer<
  UnnamedRetentionMetricsState,
  GetUnnamedRetentionMetricResponse
>({
  mergeData: (state, update) => {
    const { hash, retentionMetric } = update;
    if (!retentionMetric) return state;

    return {
      ...state,
      [hash]: retentionMetric,
    };
  },
})(
  RetentionMetricsRequestTypes.GetUnnamedRetentionMetricRequest,
  RetentionMetricsRequestTypes.GetUnnamedRetentionMetricSuccess,
  RetentionMetricsRequestTypes.GetUnnamedRetentionMetricFailure,
);

type CreateUnnamedRetentionMetricRequestAction = RequestAction<
  RetentionMetricsRequestTypes.CreateUnnamedRetentionMetricRequest,
  CreateUnnamedRetentionMetricRequest
>;
const isCreateUnnamedRetentionMetricRequestAction = (
  action: Action,
): action is CreateUnnamedRetentionMetricRequestAction =>
  action.type === RetentionMetricsRequestTypes.CreateUnnamedRetentionMetricRequest;

type UnnamedRetentionMetricsRequestState = ReturnType<typeof unnamedRetentionMetricsRequests>;
const unnamedRetentionMetrics = (
  state: UnnamedRetentionMetricsRequestState = {
    lastRequested: -1,
    lastResponded: -1,
    loading: false,
    error: false,
    data: null,
    safeMessage: null,
  },
  action: CreateUnnamedRetentionMetricRequestAction | DispatchedAction<any, any, any, any, any>,
): UnnamedRetentionMetricsRequestState => {
  if (isCreateUnnamedRetentionMetricRequestAction(action)) {
    if (!action.body || !action.body.unnamedRetentionMetric) return state;
    return {
      ...state,
      data: {
        ...state.data,
        [action.body.hash]: action.body.unnamedRetentionMetric,
      },
    };
  }
  return unnamedRetentionMetricsRequests(state, action);
};

const access = createAccessReducer(
  RetentionMetricsRequestTypes.RetentionMetricsAccessRequest,
  RetentionMetricsRequestTypes.RetentionMetricsAccessSuccess,
  RetentionMetricsRequestTypes.RetentionMetricsAccessFailure,
);

type RetentionMetricsReducerState = {
  allRetentionMetrics: ReturnType<typeof allRetentionMetrics>;
  unnamedRetentionMetrics: ReturnType<typeof unnamedRetentionMetrics>;
  retentionMetrics: ReturnType<typeof retentionMetrics>;
  data: ReturnType<typeof data>;
  access: ReturnType<typeof access>;
};

export const retentionMetricsReducer = combineReducers<RetentionMetricsReducerState>({
  allRetentionMetrics,
  unnamedRetentionMetrics,
  retentionMetrics,
  data,
  access,
});
